<main class="survey-admin survey-edit" role="main" aria-label="Edit survey">
  <header class="survey-admin__header">
    <h1 class="survey-admin__title">{{ typeName || 'Survey' }}</h1>
    <p class="survey-admin__desc">Edit questions, answers, setup prompt, and call-to-action.</p>
  </header>

  <!-- Survey name: editable after creation -->
  @if (!loading && typeId) {
    <section class="survey-edit__section survey-edit__section--name" aria-labelledby="survey-name-heading">
      <h2 id="survey-name-heading" class="survey-edit__section-title">Survey name</h2>
      <div class="survey-form__group survey-form__group--inline">
        <label class="survey-form__label" for="survey-type-name">Name shown to respondents</label>
        <input
          class="survey-form__input survey-form__input--wide"
          type="text"
          id="survey-type-name"
          [(ngModel)]="editingTypeName"
          (blur)="saveSurveyName()"
          placeholder="Survey name"
          maxlength="200"
        />
        @if (nameSaveError) {
          <p class="survey-form__error" role="alert">{{ nameSaveError }}</p>
        }
      </div>
    </section>
  }

  <!-- Version status and published survey URL (no action buttons here; those are at bottom) -->
  @if (!loading && version) {
    <section class="survey-edit__section survey-edit__section--actions" aria-labelledby="publish-preview-heading">
      <h2 id="publish-preview-heading" class="survey-edit__section-title">Version &amp; link</h2>
      <p class="survey-form__hint">
        Version status: <strong>{{ versionStateCode }}</strong>.
        @if (isDraft) {
          Publish from the buttons below to make this version available for respondents.
        } @else {
          This version is {{ versionStateCode.toLowerCase() }}.
        }
      </p>
      @if (!isDraft) {
        <div class="survey-form__group survey-edit__url-block">
          <label class="survey-form__label" for="survey-url">Survey URL (share this link to start a new survey)</label>
          <div class="survey-edit__url-row">
            <input
              type="text"
              id="survey-url"
              class="survey-form__input survey-form__input--wide survey-form__input--readonly"
              readonly
              [value]="publishedSurveyUrl"
              aria-describedby="survey-url-desc"
            />
            <button type="button" class="btn btn-primary survey-edit__copy-url-btn" (click)="copySurveyUrl()" aria-label="Copy survey URL to clipboard">
              Copy
            </button>
          </div>
          @if (copyUrlFeedback) {
            <p class="survey-form__hint survey-edit__copy-feedback" role="status" aria-live="polite">{{ copyUrlFeedback }}</p>
          }
          <p id="survey-url-desc" class="survey-form__hint">Use this URL in iframes or ads; opening it creates a new survey run and shows the survey to the respondent.</p>
        </div>
      }
    </section>
  }

  @if (errorMessage) {
    <p class="survey-form__error" role="alert">{{ errorMessage }}</p>
  }
  @if (loading) {
    <p role="status" aria-live="polite">Loading...</p>
  }

  @if (!loading && version && editLockBlockedByOther) {
    <section class="survey-edit__section survey-edit__section--readonly-banner survey-edit__section--lock-banner" role="status" aria-live="polite">
      <p class="survey-edit__readonly-banner">
        <strong>Other admin is currently editing.</strong>
        @if (lockedByUserName) {
          <span> Locked by {{ lockedByUserName }}.</span>
        }
        <span> This version is read-only until they leave or the lock expires.</span>
      </p>
    </section>
  }
  @if (!loading && version && !isDraft && !editLockBlockedByOther) {
    <section class="survey-edit__section survey-edit__section--readonly-banner" role="status" aria-live="polite">
      <p class="survey-edit__readonly-banner">
        <strong>This version is {{ versionStateCode }}.</strong> Questions, answers, and structure cannot be changed. From the survey types list, use <strong>View versions</strong> and create a draft from this version to edit.
      </p>
    </section>
  }

  <!-- Must preview before publish (admin must preview a survey before publishing it). -->
  @if (!loading && version && isDraft && !hasBeenPreviewed) {
    <section class="survey-edit__section survey-edit__section--preview-required" role="status" aria-live="polite">
      <p class="survey-edit__preview-required-banner">
        <strong>Preview required.</strong> You must preview this survey before you can publish it. Use the <strong>Preview survey</strong> button below, then return here to publish.
      </p>
    </section>
  }

  <!-- Setup prompt (tied to survey / version). Editable only when draft and we hold the edit lock. -->
  <section class="survey-edit__section" aria-labelledby="setup-prompt-heading">
    <h2 id="setup-prompt-heading" class="survey-edit__section-title">Setup prompt</h2>
    @if (canEditVersion) {
    <div class="survey-form__group survey-form__group--label-on-top survey-edit__prompt-row">
      <label class="survey-form__label survey-form__label--static" for="setup-prompt">Prompt text sent to the LLM with this survey</label>
      <textarea
        class="survey-form__textarea survey-form__textarea--setup"
        id="setup-prompt"
        name="setupPrompt"
        rows="4"
        placeholder="e.g. Review the below questions, answers and score and provide an analysis..."
        aria-describedby="setup-prompt-desc"
        [(ngModel)]="setupPrompt"
        (blur)="save()"
      ></textarea>
      <div class="survey-edit__improve-row">
        <button type="button" class="btn btn-primary survey-edit__improve-btn" (click)="improvePrompt('setup')" [disabled]="!setupPrompt?.trim() || improveInProgress" [attr.aria-busy]="improveInProgress" aria-label="Get an improved version of this prompt">
          {{ improveInProgress && improveTarget === null ? 'Improving...' : 'Improve this prompt!' }}
        </button>
      </div>
      @if (improveTarget === 'setup' && improveSuggested) {
        <div class="survey-edit__improve-result">
          <label class="survey-form__label">Recommended prompt</label>
          <textarea class="survey-form__textarea survey-form__textarea--sm survey-form__input--readonly" rows="3" readonly [value]="improveSuggested"></textarea>
          <div class="survey-edit__improve-actions">
            <button type="button" class="btn btn-primary" (click)="useImprove()">Use this prompt</button>
            <button type="button" class="btn survey-edit__revert-btn" (click)="revertImprove()">Revert</button>
          </div>
        </div>
      }
      @if (improveError) {
        <p class="survey-form__error" role="alert">{{ improveError }}</p>
      }
      <p id="setup-prompt-desc" class="survey-form__hint">This text is included at the start of the prompt sent to the LLM.</p>
    </div>
    } @else if (version) {
    <div class="survey-edit__readonly-text">{{ setupPrompt || '(None)' }}</div>
    }
  </section>

  <!-- Scoring guide: conditional to survey & survey version. -->
  <section class="survey-edit__section" aria-labelledby="scoring-guide-heading">
    <h2 id="scoring-guide-heading" class="survey-edit__section-title">Scoring guide</h2>
    <p class="survey-form__hint">Optional title and content for this survey version (e.g. how scores are interpreted). Shown to admins; not sent to the LLM.</p>
    @if (canEditVersion) {
    <div class="survey-form__group">
      <label class="survey-form__label" for="scoring-guide-title">Title</label>
      <input
        class="survey-form__input survey-form__input--wide"
        type="text"
        id="scoring-guide-title"
        [(ngModel)]="scoringGuideTitle"
        (blur)="save()"
        placeholder="e.g. Score interpretation"
        maxlength="200"
        name="scoringGuideTitle"
      />
    </div>
    <div class="survey-form__group">
      <label class="survey-form__label" for="scoring-guide-content">Content</label>
      <textarea
        class="survey-form__textarea"
        id="scoring-guide-content"
        rows="5"
        [(ngModel)]="scoringGuideContent"
        (blur)="save()"
        placeholder="Describe how scores are calculated or what ranges mean..."
        name="scoringGuideContent"
      ></textarea>
    </div>
    } @else if (version) {
    <div class="survey-edit__readonly-text">
      @if (scoringGuideTitle) { <strong>{{ scoringGuideTitle }}</strong> }
      @if (scoringGuideContent) { <p>{{ scoringGuideContent }}</p> }
      @if (!scoringGuideTitle && !scoringGuideContent) { <span>(None)</span> }
    </div>
    }
  </section>

  <!-- Collect contact info. Editable only when draft and we hold the edit lock. -->
  <section class="survey-edit__section" aria-labelledby="contact-heading">
    <h2 id="contact-heading" class="survey-edit__section-title">Contact information</h2>
    @if (canEditVersion) {
    <div class="survey-form__group">
      <label class="survey-form__checkbox-wrap">
        <input type="checkbox" name="collectContactInfo" class="survey-form__checkbox" aria-describedby="contact-desc" [(ngModel)]="collectContactInfo" (change)="save()" />
        <span class="survey-form__checkbox-label">Collect contact information (Name, Email, Phone, Business Name)</span>
      </label>
      <p id="contact-desc" class="survey-form__hint">When enabled, the answer page will show contact fields. At least email or phone is required.</p>
    </div>
    } @else if (version) {
    <p class="survey-edit__readonly-text">Collect contact information: {{ collectContactInfo ? 'Yes' : 'No' }}</p>
    }
  </section>

  <!-- Questions: editable only when draft and we hold the edit lock; read-only when published/archived or lock held by other -->
  <section class="survey-edit__section" aria-labelledby="questions-heading">
    <h2 id="questions-heading" class="survey-edit__section-title">Questions</h2>

    @if (canEditVersion) {
    <button type="button" class="btn btn-primary survey-edit__add-btn" (click)="addQuestion()">
      Add question
    </button>

    <div class="survey-edit__questions-list" cdkDropList (cdkDropListDropped)="onQuestionDrop($event)" cdkDropListOrientation="vertical">
      @for (question of questions; track question.id) {
        <div class="survey-edit__question" [attr.data-question-id]="question.id" cdkDrag [class.survey-edit__question--collapsed]="!isQuestionExpanded(question.id)">
          <fieldset class="survey-edit__question-fieldset">
          <legend class="survey-edit__question-legend">
            <span class="survey-edit__drag-handle" cdkDragHandle aria-label="Drag to reorder question">⋮⋮</span>
            <button type="button" class="survey-edit__question-toggle" (click)="toggleQuestionCollapsed(question.id)" [attr.aria-expanded]="isQuestionExpanded(question.id)" [attr.aria-controls]="'q-content-' + question.id" aria-label="Toggle question details">
              <span class="survey-edit__question-toggle-icon" [class.survey-edit__question-toggle-icon--collapsed]="!isQuestionExpanded(question.id)" aria-hidden="true">▼</span>
            </button>
            <span class="survey-edit__question-heading">
              <span class="survey-edit__question-number">Question {{ question.displayOrder + 1 }}</span>
              @if (!isQuestionExpanded(question.id) && question.questionText) {
                <span class="survey-edit__question-preview">— {{ question.questionText }}</span>
              }
            </span>
          </legend>
          @if (isQuestionExpanded(question.id)) {
          <div class="survey-edit__question-content" [id]="'q-content-' + question.id">
          <div class="survey-form__group">
            <label class="survey-form__label" [for]="'q-text-' + question.id">Question text</label>
            <input
              class="survey-form__input"
              type="text"
              [id]="'q-text-' + question.id"
              [(ngModel)]="question.questionText"
              (blur)="updateQuestion(question)"
              placeholder="Question text"
            />
          </div>
          <div class="survey-form__group">
            <label class="survey-form__label" [for]="'q-type-' + question.id">Type</label>
            <select
              class="survey-form__select"
              [id]="'q-type-' + question.id"
              [(ngModel)]="question.questionType"
              (change)="updateQuestion(question)"
            >
              <option value="Radio">Radio buttons</option>
              <option value="FreeFormText">Free-form text</option>
            </select>
          </div>
          @if (question.questionType === 'FreeFormText') {
            <div class="survey-form__group survey-edit__max-chars">
              <label class="survey-form__label" [for]="'q-maxchars-' + question.id">Max character count</label>
              <input
                class="survey-form__input survey-form__input--narrow"
                type="number"
                [id]="'q-maxchars-' + question.id"
                [(ngModel)]="question.maxCharacterCount"
                (blur)="updateQuestion(question)"
                min="1"
                max="2000"
              />
            </div>
          }
          <div class="survey-edit__answers">
            <h3 class="survey-edit__answers-title">Answers</h3>
            <button type="button" class="btn survey-edit__add-answer-btn" (click)="addAnswer(question)">
              Add answer
            </button>
            <ul class="survey-edit__answers-list" role="list" cdkDropList (cdkDropListDropped)="onAnswerDrop(question.id, $event)" cdkDropListOrientation="vertical">
              @for (answer of getAnswers(question.id); track answer.id) {
                <li class="survey-edit__answer-item" cdkDrag>
                  <span class="survey-edit__drag-handle survey-edit__drag-handle--answer" cdkDragHandle aria-label="Drag to reorder answer">⋮⋮</span>
                  <div class="survey-form__group survey-form__group--inline">
                    <label class="survey-form__label" [for]="'a-text-' + answer.id">Answer text</label>
                    <input
                      class="survey-form__input"
                      type="text"
                      [id]="'a-text-' + answer.id"
                      [(ngModel)]="answer.answerText"
                      (blur)="updateAnswer(answer)"
                      placeholder="Answer text"
                    />
                    <label class="survey-form__label survey-form__label--score" [for]="'a-score-' + answer.id">Score</label>
                    <input
                      class="survey-form__input survey-form__input--tiny"
                      type="number"
                      [id]="'a-score-' + answer.id"
                      [(ngModel)]="answer.score"
                      (blur)="updateAnswer(answer)"
                      min="0"
                      step="1"
                    />
                  </div>
                  <button type="button" class="btn survey-edit__remove-btn" aria-label="Remove this answer" (click)="removeAnswer(answer)">
                    Remove
                  </button>
                </li>
              }
            </ul>
          </div>
          <div class="survey-edit__fragments">
            <h3 class="survey-edit__fragments-title">Prompt fragments</h3>
            <p class="survey-form__hint">Fixed text added to the LLM prompt when specific answers are selected.</p>
            <button type="button" class="btn survey-edit__add-fragment-btn" (click)="addFragment(question)">
              Add fragment
            </button>
            <ul class="survey-edit__fragments-list" role="list">
              @for (frag of fragments; track frag.id) {
                @if (frag.surveyQuestionId === question.id) {
                  <li class="survey-edit__fragment-item">
                    <textarea
                      class="survey-form__textarea survey-form__textarea--sm"
                      rows="2"
                      [(ngModel)]="frag.fragmentText"
                      (blur)="updateFragment(frag)"
                      placeholder="Fragment text..."
                    ></textarea>
                    <div class="survey-edit__improve-row">
                      <button type="button" class="btn btn-primary survey-edit__improve-btn" (click)="improvePrompt('frag-' + frag.id)" [disabled]="!frag.fragmentText?.trim() || improveInProgress" [attr.aria-busy]="improveInProgress">Improve this prompt!</button>
                    </div>
                    @if (improveTarget === 'frag-' + frag.id && improveSuggested) {
                      <div class="survey-edit__improve-result">
                        <label class="survey-form__label">Recommended</label>
                        <textarea class="survey-form__textarea survey-form__textarea--sm survey-form__input--readonly" rows="2" readonly [value]="improveSuggested"></textarea>
                        <div class="survey-edit__improve-actions">
                          <button type="button" class="btn btn-primary" (click)="useImprove()">Use this prompt</button>
                          <button type="button" class="btn survey-edit__revert-btn" (click)="revertImprove()">Revert</button>
                        </div>
                      </div>
                    }
                    <p class="survey-form__hint">Trigger answers: {{ getFragmentTriggers(frag.id).length }} selected</p>
                    <select class="survey-form__select survey-form__select--short" #triggerSelect (change)="onAddFragmentTrigger(frag, triggerSelect); triggerSelect.value = ''">
                      <option value="">Add trigger answer...</option>
                      @for (ans of getAnswers(question.id); track ans.id) {
                        <option [value]="ans.id">{{ ans.answerText }}</option>
                      }
                    </select>
                    <div class="survey-edit__trigger-tags" role="list">
                      @for (tr of getFragmentTriggers(frag.id); track tr.surveyAnswerId) {
                        <div class="survey-edit__trigger-tag">
                          {{ getAnswerText(question.id, tr.surveyAnswerId) }}
                          <button type="button" class="btn survey-edit__remove-btn" (click)="removeFragmentTrigger(frag.id, tr.surveyAnswerId)">Remove</button>
                        </div>
                      }
                    </div>
                    <button type="button" class="btn survey-edit__remove-btn" aria-label="Remove this fragment" (click)="removeFragment(frag)">
                      Remove fragment
                    </button>
                  </li>
                }
              }
            </ul>
          </div>
          <button type="button" class="btn survey-edit__remove-question-btn" aria-label="Remove this question" (click)="removeQuestion(question)">
            Remove question
          </button>
          </div>
          }
        </fieldset>
        </div>
      }
    </div>
    <button type="button" class="btn btn-primary survey-edit__add-btn survey-edit__add-btn--bottom" (click)="addQuestion()" aria-label="Add another question">
      Add question
    </button>
    } @else if (version) {
    <ul class="survey-edit__questions-readonly" role="list">
      @for (question of questions; track question.id) {
        <li class="survey-edit__question-readonly">
          <strong>Question {{ question.displayOrder + 1 }}</strong> ({{ question.questionType }})<br />
          {{ question.questionText || '(No text)' }}
          @if (question.questionType === 'FreeFormText' && question.maxCharacterCount) {
            <span class="survey-edit__readonly-hint"> — max {{ question.maxCharacterCount }} chars</span>
          }
          <ul class="survey-edit__answers-readonly" role="list">
            @for (answer of getAnswers(question.id); track answer.id) {
              <li>{{ answer.answerText }} (score: {{ answer.score }})</li>
            }
          </ul>
          @for (frag of fragments; track frag.id) {
            @if (frag.surveyQuestionId === question.id) {
              <div class="survey-edit__fragment-readonly">
                <span class="survey-edit__fragment-readonly-label">Prompt fragment:</span>
                <span class="survey-edit__fragment-readonly-text">{{ frag.fragmentText || '(None)' }}</span>
                @if (getFragmentTriggers(frag.id).length) {
                  <span class="survey-edit__readonly-hint"> — when selected: @for (tr of getFragmentTriggers(frag.id); track tr.surveyAnswerId) { {{ getAnswerText(question.id, tr.surveyAnswerId) }}@if (!$last) {, } }</span>
                }
              </div>
            }
          }
        </li>
      }
    </ul>
    }
  </section>

  <section class="survey-edit__section" aria-labelledby="cta-heading">
    <h2 id="cta-heading" class="survey-edit__section-title">Call-to-action thresholds</h2>
    <p class="survey-form__hint">Display text and prompt fragment based on total score (e.g. if score >= 40 show one message).</p>
    @if (canEditVersion) {
    <button type="button" class="btn btn-primary survey-edit__add-btn" (click)="addThreshold()">
      Add threshold
    </button>
    <ul class="survey-edit__thresholds-list" role="list">
      @for (t of thresholds; track t.id) {
        <li class="survey-edit__threshold-item">
          <div class="survey-form__group">
            <label class="survey-form__label">Condition</label>
            <select class="survey-form__select survey-form__select--short" [(ngModel)]="t.thresholdOperator" (change)="updateThreshold(t)">
              <option value=">=">Score &gt;=</option>
              <option value="<">Score &lt;</option>
              <option value=">">Score &gt;</option>
              <option value="<=">Score &lt;=</option>
            </select>
            <input
              class="survey-form__input survey-form__input--tiny"
              type="number"
              [(ngModel)]="t.thresholdValue"
              (blur)="updateThreshold(t)"
              min="0"
            />
          </div>
          <div class="survey-form__group">
            <label class="survey-form__label">Display text</label>
            <input
              class="survey-form__input"
              type="text"
              [(ngModel)]="t.displayText"
              (blur)="updateThreshold(t)"
              placeholder="Shown to user on results page"
            />
          </div>
          <div class="survey-form__group">
            <label class="survey-form__label">Prompt fragment (for LLM)</label>
            <textarea
              class="survey-form__textarea survey-form__textarea--sm"
              rows="2"
              [(ngModel)]="t.promptFragmentText"
              (blur)="updateThreshold(t)"
              placeholder="Optional"
            ></textarea>
            <div class="survey-edit__improve-row">
              <button type="button" class="btn btn-primary survey-edit__improve-btn" (click)="improvePrompt('threshold-' + t.id)" [disabled]="!t.promptFragmentText?.trim() || improveInProgress" [attr.aria-busy]="improveInProgress">Improve this prompt!</button>
            </div>
            @if (improveTarget === 'threshold-' + t.id && improveSuggested) {
              <div class="survey-edit__improve-result">
                <label class="survey-form__label">Recommended</label>
                <textarea class="survey-form__textarea survey-form__textarea--sm survey-form__input--readonly" rows="2" readonly [value]="improveSuggested"></textarea>
                <div class="survey-edit__improve-actions">
                  <button type="button" class="btn btn-primary" (click)="useImprove()">Use this prompt</button>
                  <button type="button" class="btn survey-edit__revert-btn" (click)="revertImprove()">Revert</button>
                </div>
              </div>
            }
          </div>
          <button type="button" class="btn survey-edit__remove-btn" aria-label="Remove this threshold" (click)="removeThreshold(t)">
            Remove
          </button>
        </li>
      }
    </ul>
    } @else if (version) {
    <ul class="survey-edit__thresholds-readonly" role="list">
      @for (t of thresholds; track t.id) {
        <li class="survey-edit__threshold-readonly">
          Score {{ t.thresholdOperator }} {{ t.thresholdValue }} — {{ t.displayText || '(No display text)' }}
          @if (t.promptFragmentText) {
            <span class="survey-edit__readonly-hint"> — fragment: {{ t.promptFragmentText }}</span>
          }
        </li>
      }
    </ul>
    }
  </section>

  <div class="survey-form__actions survey-form__actions--bar">
    <div class="survey-form__actions-left">
      <button type="button" class="btn btn-primary survey-admin__btn" (click)="goPreview()" aria-label="Preview this survey as respondents will see it">
        Preview survey
      </button>
      @if (canEditVersion) {
        <button type="button" class="btn btn-primary" (click)="publishVersion()" [disabled]="!hasBeenPreviewed" [attr.aria-disabled]="!hasBeenPreviewed" [title]="!hasBeenPreviewed ? 'Preview this survey first to enable publish' : ''" aria-label="Publish this survey version">
          Publish survey
        </button>
      }
    </div>
    <div class="survey-form__actions-right">
      <a class="btn survey-form__cancel" routerLink="/admin/survey-types">
        Back to survey types
      </a>
      @if (canEditVersion) {
        <button type="button" class="btn btn-primary" (click)="save()" [disabled]="saving" [attr.aria-busy]="saving">
          @if (saving) {
            Saving...
          } @else if (saveSuccess) {
            Saved
          } @else {
            Save changes
          }
        </button>
      }
    </div>
  </div>
</main>
